import * as React from "react";
import { useSearchParams } from "react-router";

import { useCurrentOrgId } from "@/providers/auth/hooks";
import { useNotification } from "@/shared/components/notifications/useNotification";
import { RoboqlQuery } from "@/shared/domain/query";
import { useRoboqlSpec } from "@/shared/domain/query/hooks";
import { ROBOQL_SEARCH_PARAM } from "@/shared/domain/query/roboqlSearchParam";
import { VIEW_SEARCH_PARAM } from "@/shared/domain/views/viewSearchParam";
import { Spinner } from "@/shared/foundation/primitives";

import type { ActiveFilters } from "../Filters/FilterModel";

import styles from "./RoboqlLinkGate.module.css";

export type RoboqlFilters = Extract<ActiveFilters, { type: "roboql" }>;

/** What a `?query=` link decided for the list under a `RoboqlLinkGate`. */
export interface RoboqlLink {
  /** The filters the link's RoboQL parsed into, or `null` if it failed to. */
  filters: RoboqlFilters | null;
}

/**
 * Open a resource list filtered by the RoboQL in `?query=`, the ad-hoc
 * counterpart of a `?view=` link. Wrap a list whose filter bar is
 * RoboQL-capable, and hand the link `children` receives to `useViewFromLink`,
 * which seeds the table with it, so the list's first query already carries the
 * filter. The link is `null` when there is none to honour: no `?query=`, or a
 * `?view=` beside it.
 *
 * RoboQL only becomes filters on the server, so the list is held back, behind a
 * spinner, until the convert endpoint answers. Mounting it first would fetch an
 * unfiltered page and then replace it, the flash `?view=` seeding removed.
 *
 * The rules:
 *
 * - **`?view=` wins.** A link that names a View is ignored here, and the list
 *   opens the View as it always has. Applying both would leave the URL
 *   claiming two different filter sets.
 * - **A query that does not parse** is reported in a notification, and the
 *   list opens unfiltered. The parameter stays in the URL, as a failed
 *   `?view=` does, so the link stays visible and a refresh retries it.
 * - **The parameter lasts until the filters change.** `useRoboqlLinkSync`
 *   drops it on the customer's first change to the filters, so a refresh or a
 *   copied URL never brings back a filter the customer has since edited away.
 *   Loading a View drops it too (`useViewDeepLink`).
 * - **A new `?query=` remounts the list**, so following a second link while
 *   the list is open (from the collaboration bar, say) opens it as fresh. So
 *   does the same `?query=` coming back after the customer's edits dropped
 *   it: following the link again opens it filtered again.
 */
export function RoboqlLinkGate({
  children,
}: {
  children: (link: RoboqlLink | null) => React.ReactNode;
}) {
  const [searchParams] = useSearchParams();
  const orgId = useCurrentOrgId();
  const notify = useNotification();

  const inUrl = searchParams.has(VIEW_SEARCH_PARAM)
    ? null
    : (searchParams.get(ROBOQL_SEARCH_PARAM) ?? null);

  // The link this gate honours. It follows a link being followed, but not the
  // parameter disappearing: `useRoboqlLinkSync` drops it once the customer
  // edits the filters, and that must not remount the list they are using.
  // A link is followed when the parameter changes to a new query, or comes
  // back after it was dropped, even with the query it had before; each one
  // gets a new `generation`, which keys the list. Updated during render,
  // React's pattern for state derived from a changing input, so a followed
  // link never renders the old list for a frame.
  const [followed, setFollowed] = React.useState({
    query: inUrl,
    generation: 0,
    inUrl: inUrl !== null,
  });
  if (inUrl !== null && (inUrl !== followed.query || !followed.inUrl)) {
    setFollowed({
      query: inUrl,
      generation: followed.generation + 1,
      inUrl: true,
    });
  } else if (inUrl === null && followed.inUrl) {
    setFollowed({ ...followed, inUrl: false });
  }
  const honoured = followed.query;
  // Fixed per query: an org switch keeps the list mounted, and re-parsing in
  // the new org would hold it behind the spinner for no change on screen.
  const [parsedFor, setParsedFor] = React.useState({ query: honoured, orgId });
  if (parsedFor.query !== honoured) {
    setParsedFor({ query: honoured, orgId });
  }
  const query = honoured?.trim() || undefined;
  const { data, error } = useRoboqlSpec(query, parsedFor.orgId);

  React.useEffect(() => {
    if (error !== null) {
      notify(
        `The link's filter could not be applied: ${error.message}`,
        "error",
      );
    }
  }, [error, notify]);

  if (query !== undefined && data === undefined && error === null) {
    return (
      <div className={styles.pending}>
        <Spinner size="lg" />
      </div>
    );
  }

  let link: RoboqlLink | null = null;
  if (query !== undefined) {
    link = {
      filters:
        data === undefined
          ? null
          : { type: "roboql", query: RoboqlQuery.from(query), spec: data },
    };
  }
  return (
    <React.Fragment key={followed.generation}>{children(link)}</React.Fragment>
  );
}
